import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Tag,
  Button,
  message,
  Row,
  Col,
  Card,
  Modal,
  Select,
  Tooltip,
  Form,
  Space,
  Empty,
  Spin,
  Input,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { FaPen, FaTrashAlt } from "react-icons/fa";
import { RiAddBoxFill } from "react-icons/ri";
import dayjs from "dayjs";
import { API } from "../../utils/API";

const { Option } = Select;

interface notificationrule {
  _id: string;
  moduleName: string;
  selectedEvents: string[];
  isActive: boolean;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt?: string;
}

interface ModuleEvents {
  [key: string]: string[];
}

const NotificationConfiguration: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRule, setEditingRule]       = useState<notificationrule | null>(null);
  const [loading, setLoading]               = useState(false);
  const [rules, setRules]                   = useState<notificationrule[]>([]);
  const [form]                              = Form.useForm();
  const [selectedModule, setSelectedModule] = useState<string>("");
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [deleteId, setDeleteId]             = useState<string | null>(null);
  const [userRole, setUserRole]             = useState<any>(null);

  // Backend-driven pagination + search
  const [page, setPage]           = useState(1);
  const [total, setTotal]         = useState(0);
  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState(""); // raw input value
  const PAGE_SIZE = 10;

  const moduleEvents: ModuleEvents = {
    "Task Management": [
      "Task Created",
      "Task Updated",
    ],
    "CRM": [
      "lead created",
      "lead updated",
      // "lead deleted",
    ],
  };

  // const isTeamRole = userRole?.name?.toLowerCase().includes("team") || userRole?.slug?.toLowerCase().includes("team");

  // const modules = Object.keys(moduleEvents).filter(mod => {
  //   if (isTeamRole && mod === "CRM") return false;
  //   return true;
  // });

  // ─── Fetch (called whenever page or searchText changes) ───────────────────
  const fetchnotificationrules = useCallback(async (currentPage: number, search: string) => {
    try {
      setLoading(true);
      const response = await API.get("/backend/notifications/rule", {
        params: { page: currentPage, limit: PAGE_SIZE, search },
      });

      if (response.data.success) {
        setRules(response.data.data || []);
        setTotal(response.data.total || 0);
      } else {
        message.error(response.data.message || "Failed to fetch notification rules");
      }
    } catch (error) {
      console.error("Error fetching notification rules:", error);
      message.error("Failed to fetch notification rules");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    try {
      const data = localStorage.getItem("authData");
      if (data) {
        const parsed = JSON.parse(data);
        setUserRole(parsed.roleId || null);
      }
    } catch (err) {
      console.error("Error parsing authData", err);
    }

    document.title = "Notification Configuration";
    fetchnotificationrules(1, "");
  }, [fetchnotificationrules]);

  // Re-fetch when page changes
  useEffect(() => {
    fetchnotificationrules(page, searchText);
  }, [page]);                                   // eslint-disable-line

  // ─── Debounced search ─────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchText(searchInput);
      setPage(1);
      fetchnotificationrules(1, searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);               // eslint-disable-line

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleAddRule = () => {
    setEditingRule(null);
    setSelectedModule("");
    setSelectedEvents([]);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditRule = (rule: notificationrule) => {
    setEditingRule(rule);
    setSelectedModule(rule.moduleName);
    setSelectedEvents(rule.selectedEvents);
    form.setFieldsValue({ module: rule.moduleName, events: rule.selectedEvents });
    setIsModalVisible(true);
  };

  const handleDeleteConfirmation = (id: string) => setDeleteId(id);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      setLoading(true);
      const response = await API.delete(`/backend/notifications/${deleteId}`);
      if (response.data.success) {
        message.success("Notification rule deleted successfully!");
        // If last item on page > 1, go back one page
        const newPage = rules.length === 1 && page > 1 ? page - 1 : page;
        setPage(newPage);
        fetchnotificationrules(newPage, searchText);
      } else {
        message.error(response.data.message || "Failed to delete notification rule");
      }
      setDeleteId(null);
    } catch (error) {
      console.error("Error deleting notification rule:", error);
      message.error("Failed to delete notification rule");
    } finally {
      setLoading(false);
    }
  };

  const handleModuleChange = (mod: string) => {
    setSelectedModule(mod);
    setSelectedEvents([]);
    form.setFieldsValue({ events: [] });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const ruleData = { moduleName: values.module, selectedEvents: values.events };

      let response;
      if (editingRule) {
        response = await API.put(`/backend/notifications/${editingRule._id}`, ruleData);
      } else {
        response = await API.post("/backend/notifications", ruleData);
      }

      if (response.data.success) {
        message.success(editingRule ? "Notification rule updated successfully!" : "Notification rule added successfully!");
        setIsModalVisible(false);
        form.resetFields();
        setSelectedModule("");
        setSelectedEvents([]);
        fetchnotificationrules(page, searchText);
      } else {
        message.error(response.data.message || "Failed to save notification rule");
      }
    } catch (error) {
      console.error("Error saving notification rule:", error);
      message.error("Failed to save notification rule");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setSelectedModule("");
    setSelectedEvents([]);
    setEditingRule(null);
  };

  const handleToggleStatus = async (
    ruleId: string,
    currentStatus: boolean,
  ) => {
    try {
      const newStatus = !currentStatus;
      const data = {
        isActive: newStatus,
      };

      const response = await API.put(`/backend/notifications/${ruleId}`, data);
      
      if (response.data.success) {
        message.success(
          `Notification rule ${newStatus ? "Activated" : "Deactivated"} successfully!`,
        );

        // Update local state
        setRules((prev) =>
          prev.map((rule) =>
            rule._id === ruleId ? { ...rule, isActive: newStatus } : rule,
          ),
        );
      } else {
        message.error(response.data.message || "Failed to update notification rule status");
      }
    } catch (error) {
      console.error("Error updating notification rule status:", error);
      message.error("Failed to update notification rule status.");
    }
  };

  // ─── Render helpers ───────────────────────────────────────────────────────
  const renderSelectedEvents = (events: string[]) => {
    if (!events || events.length === 0) return <Tag color="default">No Events</Tag>;
    if (events.length === 1)            return <Tag color="blue">{events[0]}</Tag>;

    return (
      <Space>
        <Tag color="blue">{events[0]}</Tag>
        <Tooltip title={<div>{events.map((e, i) => <div key={i}>{e}</div>)}</div>}>
          <Tag color="default" style={{ cursor: "pointer" }}>+{events.length - 1}</Tag>
        </Tooltip>
      </Space>
    );
  };

  const columns: ColumnsType<notificationrule> = [
    {
      title: "Module Name",
      dataIndex: "moduleName",
      key: "moduleName",
      render: (v: string) => <Tag color="purple" style={{ fontWeight: 500 }}>{v}</Tag>,
    },
    {
      title: "Selected Events",
      dataIndex: "selectedEvents",
      key: "selectedEvents",
      render: (events: string[]) => renderSelectedEvents(events),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      align: "center" as const,
      width: 100,
      render: (isActive: boolean, record: notificationrule) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <label
            style={{
              position: "relative",
              display: "inline-block",
              width: 40,
              height: 22,
              backgroundColor: isActive ? "#001529" : "#f0f0f0",
              borderRadius: 13,
              cursor: "pointer",
              transition: "background-color 0.3s ease",
              boxShadow: isActive
                ? "inset 0 0 0 2px #001529"
                : "inset 0 0 0 1px #ccc",
            }}
          >
            <input
              type="checkbox"
              checked={isActive}
              onChange={() => handleToggleStatus(record._id, isActive)}
              style={{ display: "none" }}
            />
            <span
              style={{
                position: "absolute",
                width: 21,
                height: 19,
                left: isActive ? "calc(99% - 21px)" : 2,
                top: 1,
                backgroundColor: "#ffffff",
                borderRadius: "50%",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                transition: "left 0.3s ease, box-shadow 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: "bold",
                color: isActive ? "#001529" : "#666",
              }}
            >
              {isActive ? "ON" : "OFF"}
            </span>
          </label>
        </div>
      ),
    },
    // {
    //   title: "Created By",
    //   dataIndex: "createdByName",
    //   key: "createdByName",
    //   render: (v: string) => <span style={{ color: "#666" }}>{v}</span>,
    // },
    // {
    //   title: "Created At",
    //   dataIndex: "createdAt",
    //   key: "createdAt",
    //   render: (v: string) => <span style={{ color: "#666" }}>{dayjs(v).format("DD-MM-YYYY HH:mm")}</span>,
    // },
    {
      title: "Actions",
      key: "actions",
      align: "center" as const,
      width: 120,
      render: (_, record) => (
        <div className="d-flex" style={{ gap: "8px", alignItems: "center", justifyContent: "center", width: "100%" }}>
          <Tooltip title="Edit">
            <button
              onClick={() => handleEditRule(record)}
              className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 shadow-[0_4px_12px_rgba(139,92,246,0.3)] hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] active:translate-y-[1px] active:shadow-none text-white flex items-center justify-center transition-all duration-200 ease-in-out group"
            >
              <FaPen className="group-hover:scale-110 transition-transform duration-200" size={12} />
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button
              onClick={() => handleDeleteConfirmation(record._id)}
              className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 shadow-[0_4px_12px_rgba(244,63,94,0.3)] hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] active:translate-y-1 active:shadow-none text-white flex items-center justify-center transition-all duration-200 group"
            >
              <FaTrashAlt className="group-hover:scale-110 transition-transform" size={12} />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header & Search */}
      <Row className="m-2" align="middle" justify="space-between" gutter={[16, 16]}>
        <Col xs={24} sm={24} md={6} lg={6} xl={6}>
          <h2 className="headername">Notification</h2>
        </Col>

        {/* Right: Search + Add Button */}
        {/* <Col xs={24} md={12}>
          <div className="flex flex-col sm:flex-row gap-2 justify-end">
            <Input
              prefix={<SearchOutlined />}
              size="large"
              placeholder="Search..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ height: "33px", width: 200 }}
            />

            <Button type="primary" onClick={handleAddRule} className="allbuttons">
              <RiAddBoxFill className="button-icon" />
              <span className="button-text">Add Notification Rule</span>
            </Button>
          </div>
        </Col> */}
        <Col xs={24} md={14} lg={14} xl={14}>
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            <Input
              prefix={<SearchOutlined />}
              size="large"
              placeholder="Search..."
              allowClear
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="flex-1"
              style={{ width: "100%", height: "33px" }}
            />
            <Button type="primary" onClick={handleAddRule} className="allbuttons">
              <RiAddBoxFill className="button-icon" />
              <span className="button-text">Add Notification Rule</span>
            </Button>
          </div>
        </Col>
      </Row>

      {/* Table */}
      <Row justify="center">
        <Col xs={24}>
          <Card className="w-100">
            <Spin spinning={loading}>
              <div className="scrollbar-hide custom-scroll-table" style={{ overflowX: "auto" }}>
                {!loading && rules.length === 0 ? (
                  <Empty
                    description="No notification rules configured"
                    style={{ padding: "60px 0" }}
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : (
                  <Table
                    columns={columns}
                    dataSource={rules}
                    rowKey="_id"
                    scroll={{ x: "max-content" }}
                    pagination={{
                      pageSize: PAGE_SIZE,
                      current: page,
                      total: total,
                      showSizeChanger: false,
                      onChange: (newPage) => setPage(newPage),
                      showTotal: (t) => `Total ${t} rules`,
                    }}
                  />
                )}
              </div>
            </Spin>
          </Card>
        </Col>
      </Row>

      {/* Add / Edit Modal */}
      <Modal
        style={{ bottom: 20, top: 20, margin: "0 auto", position: "relative" }}
        styles={{
          body:    { overflowY: "auto", fontFamily: "inter, sans-serif" },
          header:  { padding: "12px", position: "sticky", top: 0, backgroundColor: "#f1f5ffff" },
          content: { padding: 0, overflow: "hidden" },
        }}
        title={
          <div style={{ color: "#4b5563", fontFamily: "inter, sans-serif", fontSize: "20px", fontWeight: 500, display: "flex", alignItems: "center", gap: "8px" }}>
            {editingRule ? "Edit Notification Rule" : "Add Notification Rule"}
            <Tooltip title="Configure notifications for specific module events">
              <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
            </Tooltip>
          </div>
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={650}
      >
        <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} style={{ padding: "24px" }}>
          <Form form={form} layout="vertical" requiredMark={false}>
            <Form.Item label="Module" name="module" rules={[{ required: true, message: "Please select a module" }]}>
              <Select placeholder="Select Module" value={selectedModule} onChange={handleModuleChange} style={{ width: "100%" }} size="large">
                {Object.keys(moduleEvents).map((mod) => <Option key={mod} value={mod}>{mod}</Option>)}
              </Select>
            </Form.Item>

            <Form.Item label="Notification Events" name="events" rules={[{ required: true, message: "Please select at least one event" }]}>
              <Select
                mode="multiple"
                placeholder="Select Notification Events"
                value={selectedEvents}
                onChange={(v) => setSelectedEvents(v)}
                disabled={!selectedModule}
                style={{ width: "100%" }}
                size="large"
                maxTagCount="responsive"
              >
                {selectedModule && moduleEvents[selectedModule]?.map((event) => (
                  <Option key={event} value={event}>{event}</Option>
                ))}
              </Select>
            </Form.Item>

            {selectedEvents.length > 0 && (
              <div style={{ marginTop: "16px" }}>
                <div style={{ fontSize: "14px", fontWeight: 500, marginBottom: "8px", color: "#262626" }}>
                  Selected Events ({selectedEvents.length}):
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {selectedEvents.map((event) => (
                    <Tag key={event} color="blue" style={{ borderRadius: "4px", padding: "4px 8px", fontSize: "12px" }}>
                      {event}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </Form>
        </div>

        <div style={{ padding: "16px 24px", borderTop: "1px solid #f0f0f0" }}>
          <div style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={handleCancel}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={loading}
                disabled={!selectedModule || selectedEvents.length === 0}
                style={{ background: "#001529", color: "#ffffff", border: "none" }}
              >
                {editingRule ? "Update" : "Save"}
              </Button>
            </Space>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteId}
        onOk={handleDelete}
        onCancel={() => setDeleteId(null)}
        okText="Yes"
        cancelText="No"
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{ bottom: 20, top: "10vw", maxHeight: "calc(100vh - 30px)", margin: "0 auto", position: "relative" }}
        title={<div style={{ marginLeft: "11px", width: "100%", paddingRight: 20 }}>Confirmation</div>}
        okButtonProps={{ style: { backgroundColor: "#ff4d4f", borderColor: "#ff4d4f", color: "white" } }}
        bodyStyle={{ padding: "22px", overflowY: "auto", fontFamily: "inter, sans-serif" }}
        styles={{
          header: { borderBottom: "1px solid #e0e0e0", padding: "12px", position: "sticky", top: 0, backgroundColor: "white" },
          content: { padding: 0, overflow: "hidden" },
          footer: { borderTop: "1px solid #e0e0e0", padding: "12px", position: "sticky", top: 0, backgroundColor: "white" },
        }}
      >
        <p>Are you sure you want to delete this notification rule?</p>
      </Modal>
    </div>
  );
};

export default NotificationConfiguration;
